import { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { Input } from './Input';
import { Button } from './Button';
import { Plus } from 'lucide-react';
import clsx from 'clsx';
import type { Band } from '../../types';

interface BandModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (bandData: BandFormData) => Promise<void>;
  band?: Band | null;
  mode?: 'create' | 'edit';
}

export interface BandFormData {
  name: string;
  short_name: string;
  styles: string[];
}

const musicStyles = [
  'Rock', 'Jazz', 'Blues', 'Folk', 'Classical', 'Pop',
  'Electronic', 'Metal', 'Punk', 'Hip Hop', 'Country', 'Reggae'
];

const initialFormData: BandFormData = {
  name: '',
  short_name: '',
  styles: [],
};

export function BandModal({ isOpen, onClose, onSubmit, band, mode = 'create' }: BandModalProps) {
  const [formData, setFormData] = useState<BandFormData>(initialFormData);
  const [newStyle, setNewStyle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (band && mode === 'edit') {
        setFormData({
          name: band.name || '',
          short_name: band.short_name || '',
          styles: band.styles || [],
        });
      } else {
        setFormData(initialFormData);
      }
      setError('');
      setNewStyle('');
    }
  }, [isOpen, band, mode]);

  const handleSubmit = async () => {
    setError('');

    if (!formData.name.trim()) {
      setError('Band name is required');
      return;
    }

    try {
      setIsSubmitting(true);
      await onSubmit(formData);
      resetAndClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || `Failed to ${mode} band`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setFormData(initialFormData);
    setNewStyle('');
    setError('');
    onClose();
  };

  const handleAddStyle = (style: string) => {
    if (style && !formData.styles.includes(style)) {
      setFormData({ ...formData, styles: [...formData.styles, style] });
      setNewStyle('');
    }
  };

  const handleRemoveStyle = (style: string) => {
    setFormData({
      ...formData,
      styles: formData.styles.filter((s) => s !== style),
    });
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      title={mode === 'edit' ? 'Edit Band' : 'Add New Band'}
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={resetAndClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            isLoading={isSubmitting}
            onClick={handleSubmit}
          >
            {mode === 'edit' ? 'Update Band' : 'Create Band'}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {error && (
          <div className="p-4 bg-red-900/20 border border-red-700/50 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        <Input
          label="Band Name *"
          type="text"
          placeholder="e.g., The Jazz Trio"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />

        <div>
          <Input
            label="Short Name"
            type="text"
            placeholder="e.g., JT"
            value={formData.short_name}
            onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
          />
          <p className="mt-1 text-xs text-dark-text-muted">
            Displayed in venue favorites. Auto-generated if left empty.
          </p>
        </div>

        {/* Music Styles */}
        <div>
          <label className="block text-sm font-medium text-dark-text-secondary mb-3">
            Music Styles
          </label>
          <div className="flex flex-wrap gap-2 mb-3">
            {musicStyles.map((style) => (
              <button
                key={style}
                type="button"
                onClick={() => handleAddStyle(style)}
                disabled={formData.styles.includes(style)}
                className={clsx(
                  'px-3 py-1.5 rounded-full text-xs font-medium',
                  formData.styles.includes(style)
                    ? 'bg-primary-900/30 text-primary-400 border border-primary-700/50'
                    : 'bg-dark-elevated text-dark-text-secondary border border-dark-border hover:bg-dark-border'
                )}
              >
                {formData.styles.includes(style) ? '✓ ' : '+ '}
                {style}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="Add custom style..."
              value={newStyle}
              onChange={(e) => setNewStyle(e.target.value)}
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => handleAddStyle(newStyle)}
              disabled={!newStyle.trim()}
            >
              <Plus className="w-4 h-4" />
              Add
            </Button>
          </div>
          {formData.styles.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {formData.styles.map((style) => (
                <span
                  key={style}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-primary-900/30 text-primary-400 border border-primary-700/50 rounded-full text-sm font-medium"
                >
                  {style}
                  <button type="button" onClick={() => handleRemoveStyle(style)}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
